import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import queryString from 'query-string';

import AuthButton from 'components/base/AuthButton';
import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import CustomListComponent from 'components/base/CustomListComponent';
import DeletePrompt from 'components/base/DeletePrompt';
import MessageCredit from 'components/message/MessageCredit';
import { ActionType, CampaignType, DELETE_RELATED_SECTIONS, PublishTagType } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import BaseListContainer from 'containers/base/BaseListContainer';
import BaseTabListContainer from 'containers/base/BaseTabListContainer';
import MessageFilter from 'containers/engagement/message/Filter';

import CampaignFilter from 'containers/engagement/campaign/Filter'
import SystemMessageFilter from 'containers/engagement/message/SystemMessageFilter';

import { checkHasPermission, createAction, getHashKeyString } from 'utils';

import './MessageList.scss';
import DeleteCampaignPrompt from 'components/campaign/campaignList/DeleteCamapignPrompt';
import { URLFilterKeys } from 'components/campaign/CampaignUrlConfig';
import { tierTypeConfig } from 'config/TierTypeConfig';

export const MESSAGE_TAB = {
  MESSAGE_CREDIT: 'Messages Credit',
  MESSAGE_CAMPAIGN: 'List of Message Campaigns',
  MESSAGE_LIST: 'List of Messages',
  SYSTEM_MESSAGE_LIST: 'List of System Messages',
};

const mapPropsToState = (state) => ({
  pageInfo: state.messageList.pageInfo,
  totalCount: state.messageList.totalCount,
  displayFields: state.messageList.listDisplayFields,
  messageList: state.messageList.currentPageMessageList,
  totalPage: state.messageList.totalPage,
  checkedList: state.messageList.checkedList,

  messageCampaignListFields: state.campaignList.listFields,
  messageCampaignDataList: state.campaignList.pagedList,
  messageCampaignPageInfo: state.campaignList.pageInfo,
  messageCampaignTotalPage: state.campaignList.totalPage,
  messageCampaignTotalCount: state.campaignList.totalCount,

  systemPageInfo: state.systemMessageList.pageInfo,
  systemTotalCount: state.systemMessageList.totalCount,
  systemDisplayFields: state.systemMessageList.listDisplayFields,
  systemMessageList: state.systemMessageList.currentPageMessageList,
  systemTotalPage: state.systemMessageList.totalPage,
  systemCheckedList: state.systemMessageList.checkedList,

  user: state.users,
});

function MessageList({
  pageInfo,
  totalCount,
  displayFields,
  messageList,
  totalPage,
  checkedList,

  messageCampaignListFields,
  messageCampaignDataList,
  messageCampaignPageInfo,
  messageCampaignTotalPage,
  messageCampaignTotalCount,

  systemPageInfo,
  systemTotalCount,
  systemDisplayFields,
  systemMessageList,
  systemTotalPage,
  systemCheckedList,

  user,
}) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [showDeleteCampaignPrompt, setShowDeleteCampaignPrompt] = useState(false);

  const activeTab =
    getHashKeyString(location.hash) || (tierTypeConfig.messages ? MESSAGE_TAB.MESSAGE_CREDIT : MESSAGE_TAB.MESSAGE_CAMPAIGN);


  const getMessageCampaignList = () => {
    let querySearch = queryString.parse(location.search);
    const searchKey = querySearch[
      URLFilterKeys.searchKey
    ];
    const stringRank = querySearch[URLFilterKeys.rank];
    const stringPage = querySearch[URLFilterKeys.page] || 1;
    const rank = stringRank === 'false';
    const page = parseInt(stringPage);

    dispatch(createAction('campaignList/getCampaignList')({
      type: CampaignType.generalMessageCampaign,
      isPageList: true,
      ...querySearch,
      page: page,
      reverse: !rank,
      searchKey: searchKey,
      isMessageCampaign: true,
    }));
  }

  useEffect(() => {
    const searchDict = queryString.parse(location.search);
    const searchKey = searchDict['search'];
    const stringRank = searchDict['rank'] || 'true';
    const stringPage = searchDict['page'] || 1;
    const rank = stringRank === 'true';
    const page = parseInt(stringPage);

    if (activeTab === MESSAGE_TAB.MESSAGE_LIST) {
      dispatch({
        type: 'messageList/getCurrentPageMessages',
        payload: {
          ...searchDict,
          page,
          rank,
          searchKey,
        },
      });
    }
    if (activeTab === MESSAGE_TAB.MESSAGE_CAMPAIGN) {
      getMessageCampaignList()
    }
    if (activeTab === MESSAGE_TAB.SYSTEM_MESSAGE_LIST) {
      dispatch({
        type: 'systemMessageList/getCurrentPageMessages',
        payload: {
          ...searchDict,
          page,
          rank,
          searchKey,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, activeTab]);

  const buttons = [
    <AuthButton
      title="Create Message Campaign"
      action={() => {
        history.push({
          pathname: '/messages/message_campaign/create',
        });
      }}
      requires={PermissionCodes.changeMessageCampaign}
      customClass="btn-further btn-download"
    />,
    ...(
      tierTypeConfig.messages
        ? [
          <AuthButton
            title="Create Message"
            action={() => {
              history.push('/messages/create');
            }}
            requires={PermissionCodes.addMessage}
          />,
        ]
        : []
    ),
  ];

  const groupActions = useMemo(() => {
    if (activeTab === MESSAGE_TAB.SYSTEM_MESSAGE_LIST) {
      return [];
    }
    if (activeTab === MESSAGE_TAB.MESSAGE_CAMPAIGN) {
      return [
        {
          name: 'Delete',
          action: (campaign) => setShowDeleteCampaignPrompt(true),
          requires: PermissionCodes.changeMessageCampaign,
        },
      ];
    }
    return [
      {
        name: 'Delete',
        action: () => {
          setShowDeletePrompt(true);
        },
        requires: PermissionCodes.deleteMessage,
      },
    ];
  }, [activeTab]);

  const tabs = [
    {
      name: MESSAGE_TAB.MESSAGE_CREDIT,
      content: <MessageCredit />,
    },
    ...checkHasPermission(user, PermissionCodes.changeMessageCampaign) ? [
      {
        name: MESSAGE_TAB.MESSAGE_CAMPAIGN,
        content: (
          <BaseTabListContainer
            hideTab={true}
            groupActions={groupActions}
            pageInfo={messageCampaignPageInfo}
            totalCount={messageCampaignTotalCount ?? 0}
            filter={{ hasFilter: true, component: CampaignFilter, displayIn: "MESSAGE_LIST" }}
            listContent={
              <BaseListContainer
                fields={messageCampaignListFields?.filter(field => field?.fieldName !== "executionPriority")}
                dataList={messageCampaignDataList}
                totalPage={messageCampaignTotalPage}
                model={'campaignList'}
                customPathname={'/messages/message_campaign'}
                actions={['Detail', 'Edit', 'Duplicate', 'Delete']}
                customPermissionMap={{
                  add: PermissionCodes.changeMessageCampaign,
                  change: PermissionCodes.changeMessageCampaign, 
                  view: PermissionCodes.changeMessageCampaign, 
                  delete: PermissionCodes.changeMessageCampaign,
                }}
                permissionGroup={PermissionCodes.message}
                deleteInfo={{
                  data: [],
                  title: 'Campaign',
                  relatedName: '',
                  onComfirm: {},
                  relatedSections: DELETE_RELATED_SECTIONS.CAMPAIGN,
                }}
                hasOtherActions={true}
                customActions={{
                  custom: (item) => {
                    if (
                      [
                        PublishTagType.expired,
                        PublishTagType.published,
                      ].includes(item.displayStatus)
                    ) {
                      return {
                        name: 'UnPublish',
                        action: (value) => {
                          dispatch(
                            createAction(`createCampaign/updateApprovalStatus`)({
                              actionType: ActionType.unpublish,
                              data: {
                                pk: value?.pk
                              },
                              afterAction: () => {
                                dispatch(createAction("createCampaign/clearData")())
                                getMessageCampaignList()
                              }
                            }),
                          );
                        },
                        requires: [
                          PermissionCodes.unPublishMessageToPending,
                          PermissionCodes.unPublishMessageToDraft,
                        ],
                        isOnlyNeedOnePermission: true
                      }
                    }
                    return null
                  }
                }}
              />
            }
          />
        )
      },
    ] : [],
    {
      name: MESSAGE_TAB.MESSAGE_LIST,
      content: (
        <BaseTabListContainer
          hideTab={true}
          groupActions={groupActions}
          pageInfo={pageInfo}
          totalCount={totalCount ? totalCount : 0}
          filter={{ hasFilter: true, component: MessageFilter }}
          listContent={
            <BaseListContainer
              fields={displayFields}
              model={'messageList'}
              permissionGroup={PermissionCodes.message}
              dataList={messageList}
              totalPage={totalPage}
              deleteInfo={{
                data: [],
                title: 'message',
                relatedName: 'channels',
                onComfirm: {},
                relatedSections: DELETE_RELATED_SECTIONS.MESSAGE,
              }}
              actions={['Edit', 'Detail', 'Duplicate', 'Delete']}
            />
          }
        />
      ),
    },
    ...checkHasPermission(user, PermissionCodes.changeSystemMessage) ? [
      {
        name: MESSAGE_TAB.SYSTEM_MESSAGE_LIST,
        content: (
          <BaseTabListContainer
            hideTab={true}
            groupActions={groupActions}
            pageInfo={systemPageInfo}
            totalCount={systemTotalCount ?? 0}
            filter={{ hasFilter: true, component: SystemMessageFilter }}
            listContent={
              <BaseListContainer
                customPathname={'messages/system_messages'}
                fields={systemDisplayFields}
                model={'systemMessageList'}
                permissionGroup={PermissionCodes.systemmessage}
                dataList={systemMessageList}
                totalPage={systemTotalPage}
                deleteInfo={{
                  data: [],
                  title: '',
                  relatedName: '',
                  onComfirm: {},
                }}
                actions={['Edit']}
              />
            }
          />
        ),
      }
    ] : [],
  ].filter(tab => {
    if (
      !tierTypeConfig.messages &&
      [
        MESSAGE_TAB.MESSAGE_CREDIT,
        MESSAGE_TAB.MESSAGE_LIST,
        MESSAGE_TAB.SYSTEM_MESSAGE_LIST,
      ].includes(tab.name)
    ) {
      return false;
    };
    return true;
  });
  return (
    <>
      <CustomListComponent
        caution={{
          detail:
            'Create a message to get in touch with  your customers. You can send a message through different channels.',
          title: 'Messages',
        }}
        defaultActiveKey={activeTab}
        buttons={buttons}
        breadcrumb={<CustomBreadcrumb />}
        hideTab={false}
        tabs={tabs}
        onTabChange={(key) => {
          history.push({
            pathname: location.pathname,
            hash: key,
          });
        }}
      />
      <DeletePrompt
        relatedSections={DELETE_RELATED_SECTIONS.MESSAGE}
        show={showDeletePrompt}
        onClose={() => setShowDeletePrompt(false)}
        onConfirm={() => {
          setShowDeletePrompt(false);
          dispatch({
            type: 'messageList/delete',
            payload: {
              afterAction: () => history.push(location),
            },
          });
        }}
        data={checkedList}
        title={'Message'}
        relatedName={'channels'}
      />
      <DeleteCampaignPrompt
        show={showDeleteCampaignPrompt}
        onClose={() => setShowDeleteCampaignPrompt(false)}
        onConfirm={() => {
          setShowDeleteCampaignPrompt(false);
          dispatch(
            createAction('createCampaign/deleteCampaigns')({
              afterAction: () => {
                history.push(location);
              },
            }),
          );
        }}
      />
    </>
  );
}

export default connect(mapPropsToState)(MessageList);
